export const GOOGLE_WORKSPACE_OAUTH_PROVIDER_SLUG = "google-workspace";
const GOOGLE_IDENTITY_SCOPES = ["openid", "email", "profile"];
export const GOOGLE_WORKSPACE_CAPABILITIES = {
    "google-calendar": {
        label: "Google Calendar",
        scopes: [
            ...GOOGLE_IDENTITY_SCOPES,
            "https://www.googleapis.com/auth/calendar.events",
            "https://www.googleapis.com/auth/calendar.calendarlist.readonly",
            "https://www.googleapis.com/auth/calendar.settings.readonly",
        ],
        sourceConnector: {
            provider: "google_calendar",
            enabledEntities: ["event"],
        },
    },
    "google-docs": {
        label: "Google Docs",
        scopes: [
            ...GOOGLE_IDENTITY_SCOPES,
            "https://www.googleapis.com/auth/documents",
            "https://www.googleapis.com/auth/drive",
        ],
    },
    "google-drive": {
        label: "Google Drive",
        scopes: [
            ...GOOGLE_IDENTITY_SCOPES,
            "https://www.googleapis.com/auth/drive",
            "https://www.googleapis.com/auth/documents",
            "https://www.googleapis.com/auth/spreadsheets",
        ],
        sourceConnector: {
            provider: "google_drive",
            enabledEntities: ["file"],
        },
    },
    "google-gmail": {
        label: "Gmail",
        scopes: [
            ...GOOGLE_IDENTITY_SCOPES,
            "https://www.googleapis.com/auth/gmail.modify",
        ],
        sourceConnector: {
            provider: "gmail",
            enabledEntities: ["message"],
        },
    },
    "google-sheets": {
        label: "Google Sheets",
        scopes: [
            ...GOOGLE_IDENTITY_SCOPES,
            "https://www.googleapis.com/auth/spreadsheets",
            "https://www.googleapis.com/auth/drive",
        ],
        sourceConnector: {
            provider: "google_sheets",
            enabledEntities: ["sheet_row"],
        },
    },
};
export const GOOGLE_WORKSPACE_PLUGIN_SLUGS = Object.freeze(Object.keys(GOOGLE_WORKSPACE_CAPABILITIES));
export function isGoogleWorkspacePluginSlug(value) {
    return Object.prototype.hasOwnProperty.call(GOOGLE_WORKSPACE_CAPABILITIES, value);
}
export function googleWorkspaceCapability(pluginSlug) {
    if (!isGoogleWorkspacePluginSlug(pluginSlug)) {
        throw new Error(`Unsupported Google Workspace plugin capability: ${pluginSlug}.`);
    }
    return GOOGLE_WORKSPACE_CAPABILITIES[pluginSlug];
}
/** Drive's grant already includes the Docs and Sheets scopes used by its composition. */
export function googleWorkspaceCredentialPluginSlugs(pluginSlug) {
    return pluginSlug === "google-drive"
        ? ["google-drive", "google-docs", "google-sheets"]
        : [pluginSlug];
}
/** Historical unbound grants retain their existing path until explicitly migrated. */
export function assertGoogleWorkspaceCredentialIssuer(credential, clientId) {
    if (!Object.hasOwn(credential, "issuer_client_id"))
        return;
    const issuer = credential.issuer_client_id;
    if (typeof issuer !== "string" || !issuer.trim()) {
        throw new Error("Google OAuth grant issuer identity is malformed.");
    }
    if (issuer !== clientId) {
        throw new Error("Google OAuth grant issuer does not match the configured client. Operator configuration repair is required.");
    }
}
export function parseGoogleConnectorCredentials(values) {
    const rawJson = values.GOOGLE_WORKSPACE_CLI_CREDENTIALS_JSON;
    if (rawJson) {
        const parsed = JSON.parse(rawJson);
        const refreshToken = String(parsed.refresh_token ?? "");
        if (parsed.type === "mlx_google_workspace_refresh_token") {
            const clientId = values.GOOGLE_CLIENT_ID ?? String(parsed.client_id ?? "");
            assertGoogleWorkspaceCredentialIssuer(parsed, clientId);
            const clientSecret = values.GOOGLE_CLIENT_SECRET ?? String(parsed.client_secret ?? "");
            if (clientId && clientSecret && refreshToken) {
                return { clientId, clientSecret, refreshToken };
            }
            if (Object.hasOwn(parsed, "issuer_client_id")) {
                throw new Error("Google OAuth bound grant is missing required credentials.");
            }
        }
        const clientId = String(parsed.client_id ?? "");
        const clientSecret = String(parsed.client_secret ?? "");
        if (clientId && clientSecret && refreshToken) {
            return { clientId, clientSecret, refreshToken };
        }
    }
    const clientId = values.GOOGLE_CLIENT_ID ?? "";
    const clientSecret = values.GOOGLE_CLIENT_SECRET ?? "";
    const refreshToken = values.GOOGLE_REFRESH_TOKEN ?? "";
    if (!clientId || !clientSecret || !refreshToken) {
        throw new Error("Google source connectors require GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, and GOOGLE_REFRESH_TOKEN connector secrets");
    }
    return { clientId, clientSecret, refreshToken };
}
